import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Button, Well, Drawer } from '@pushly/aqe/lib/components'
import { EditOutlined, MessageOutlined } from '@ant-design/icons'
import { Flex, Input, Skeleton, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import * as randomstring from 'randomstring'
import { AppService, AiGenerationDirectiveService } from '../../services'
import { getClassNames } from '../../_utils/classnames'
import { simpleNotification } from '../../_utils/utils'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'
import { BrandVoiceDecomposition, BrandVoiceSummaryChips, InfoRow, CompileState } from './brand-voice-decomposition'

const COMPILE_DEBOUNCE_MS = 600

const PLACEHOLDER =
    'e.g. Write like a trusted editor. Never use exclamation points. Keep titles under 60 characters. Favor curiosity over hype.'

interface IBrandVoiceEditor {
    domainId: number
    directive: AiGenerationDirective | null
    visible: boolean
    onClose: (e: any) => void
    onSaved: (directive: AiGenerationDirective) => void
}

const BrandVoiceEditor: FC<IBrandVoiceEditor> = (props) => {
    const appSvc = useService(AppService)
    const directiveSvc = useService(AiGenerationDirectiveService)

    const initialBody = props.directive?.active_version?.body ?? ''
    const initialCompiled = props.directive?.active_version?.compiled_rules ?? null

    const [body, setBody] = useState<string>(initialBody)
    const [isDirty, setIsDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [compileState, setCompileState] = useState<CompileState>(initialCompiled ? 'done' : 'idle')
    const [compiled, setCompiled] = useState<CompiledDirective | null>(initialCompiled)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()
    // The body whatever `compiled` currently holds actually reflects (a persisted result on
    // load, or the last successful recompile) — lets the effect below tell "the author edited
    // the text" apart from "the component re-rendered with the same text", so opening the
    // drawer on a directive with a saved decomposition never fires a redundant compile call.
    const lastCompiledBodyRef = useRef<string | undefined>(initialCompiled ? initialBody : undefined)

    const runCompile = useCallback(
        async (text: string) => {
            if (!text.trim()) {
                setCompiled(null)
                setCompileState('idle')
                lastCompiledBodyRef.current = undefined
                return
            }
            setCompileState('compiling')
            const res = await directiveSvc.compilePreview(props.domainId, text)
            if (res.ok && res.data) {
                setCompiled(res.data)
                setCompileState('done')
                lastCompiledBodyRef.current = text
            } else {
                setCompileState('error')
            }
        },
        [directiveSvc, props.domainId],
    )

    // Recompile (debounced) only once the text actually diverges from whatever `compiled`
    // currently reflects — never merely because the drawer opened or re-rendered.
    useEffect(() => {
        if (body === lastCompiledBodyRef.current) return
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runCompile(body), COMPILE_DEBOUNCE_MS)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [body, runCompile])

    const handleSubmit = async () => {
        const trimmed = body.trim()
        if (!trimmed) {
            simpleNotification('error', 'Brand Voice cannot be empty')
            return
        }
        if (!compiled) {
            // `disableSubmit` below should prevent reaching here without a completed, current
            // compile — defensive guard, not the primary gate.
            simpleNotification('error', 'Please wait for the Brand Voice to finish analyzing before saving')
            return
        }

        setSaving(true)
        try {
            const res = props.directive
                ? await directiveSvc.update(
                      props.domainId,
                      props.directive.id,
                      UpdateAiGenerationDirectiveRequest.from({ body: trimmed }),
                  )
                : await directiveSvc.create(
                      props.domainId,
                      CreateAiGenerationDirectiveRequest.from({
                          role: 'brand_voice',
                          name: 'Brand Voice',
                          body: trimmed,
                      }),
                  )

            if (!res.ok || !res.data) return

            // Persist the EXACT decomposition just displayed and approved (`compiled`) — the
            // server independently recompiles and only writes it if its own fresh result
            // matches, so the next time this directive opens, its saved rules are there to
            // show immediately. Reuses compilePreview's Redis pre-warm from the debounced edit
            // above, so this is typically instant.
            const compiledRes = await directiveSvc.compileAndPersist(props.domainId, res.data.id, compiled)

            if (compiledRes.ok && compiledRes.data) {
                simpleNotification('success', 'Brand Voice was successfully updated')
                props.onSaved(compiledRes.data)
            } else {
                // The server's fresh recompile no longer matched what was reviewed (an expired
                // preview cache, a compiler change, or a stale review) — the text itself saved,
                // but the reviewed decomposition was NOT persisted rather than silently
                // persisting something different from what was approved.
                simpleNotification(
                    'warning',
                    "Brand Voice text was saved, but its rules changed since you last reviewed them. Reopen to re-check before they're enforced.",
                )
                props.onSaved(res.data)
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-brand-voice-editor')}
            title="Update Brand Voice"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            // Never enabled mid-compile or against stale/errored text — Submit only becomes
            // available once `compiled` reflects a successfully-finished compile of the exact
            // text on screen, so what gets persisted is guaranteed to be what was reviewed.
            disableSubmit={!isDirty || saving || compileState !== 'done' || body !== lastCompiledBodyRef.current}
            onClose={props.onClose}
            open={props.visible}
            size={960}
        >
            <Flex vertical gap={16} className={getClassNames('brand-voice-editor-body')}>
                <Typography.Text type="secondary">
                    Free-text guidance, split into rules we can guarantee and style we apply best-effort.
                </Typography.Text>

                <Flex gap={24} align="flex-start" wrap="wrap">
                    <Flex vertical gap={6} style={{ flex: 1, minWidth: 280, maxWidth: 380 }}>
                        <Typography.Text strong>Your Brand Voice</Typography.Text>
                        <Input.TextArea
                            value={body}
                            onChange={(e) => {
                                setBody(e.target.value)
                                setIsDirty(true)
                            }}
                            autoSize={{ minRows: 12, maxRows: 24 }}
                            placeholder={PLACEHOLDER}
                        />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Edit here — the decomposition updates as you type.
                        </Typography.Text>
                    </Flex>
                    <div style={{ flex: 2, minWidth: 340, paddingTop: 28 }}>
                        <BrandVoiceDecomposition
                            state={compileState}
                            compiled={compiled}
                            onRetry={() => runCompile(body)}
                        />
                    </div>
                </Flex>
            </Flex>
        </Drawer>
    )
}

interface IBrandVoiceWell {
    domainId: number
    canEdit: boolean
}

export const BrandVoiceWell: FC<IBrandVoiceWell> = (props) => {
    const directiveSvc = useService(AiGenerationDirectiveService)

    const [loading, setLoading] = useState(true)
    const [directive, setDirective] = useState<AiGenerationDirective | null>(null)
    const [showDrawer, setShowDrawer] = useState(false)
    const [drawerKey, setDrawerKey] = useState<string>('')

    const loadBrandVoice = async () => {
        setLoading(true)
        const res = await directiveSvc.fetchBrandVoice(props.domainId)
        if (res.ok) {
            setDirective(res.data ?? null)
        }
        setLoading(false)
    }

    useEffect(() => {
        loadBrandVoice()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.domainId])

    const body = directive?.active_version?.body
    const compiledRules = directive?.active_version?.compiled_rules ?? null

    return (
        <Well
            className="nested"
            title="Brand Voice"
            actions={
                <Button
                    size="small"
                    onClick={() => {
                        setDrawerKey(randomstring.generate())
                        setShowDrawer(true)
                    }}
                    disabled={!props.canEdit || loading}
                >
                    <EditOutlined />
                    <span>Edit</span>
                </Button>
            }
            footerMode="hidden"
        >
            <Skeleton loading={loading} active={true} title={false}>
                {!loading &&
                    (body ? (
                        <Flex vertical gap={10}>
                            {/* Leads with what's actually structured/enforced — the free-text
                                body explains what was SAID, not what got ENFORCED, so it's
                                demoted to supporting detail once we have something to
                                summarize. Falls back to text-only for a directive saved before
                                compiled_rules was reliably persisted (or mid-first-compile). */}
                            {compiledRules && <BrandVoiceSummaryChips compiled={compiledRules} />}
                            {compiledRules ? (
                                // Demoted below the chips by size and a neutral card, not by
                                // washing the color out (antd's `type="secondary"` reads too
                                // faint for a full paragraph) — still fully legible, but reads
                                // as a quoted excerpt of what was typed (matching the app's own
                                // neutral "summary card" pattern, e.g. Vespa's recommendation
                                // config "Configuration Summary" box) rather than as this
                                // card's primary content or app-authored copy.
                                <div style={{ maxWidth: 640 }}>
                                    <InfoRow
                                        icon={<MessageOutlined />}
                                        style={{ backgroundColor: 'var(--color-gray-light)' }}
                                    >
                                        <Typography.Paragraph
                                            style={{ whiteSpace: 'pre-wrap', marginBottom: 0, fontSize: 13 }}
                                        >
                                            {body}
                                        </Typography.Paragraph>
                                    </InfoRow>
                                </div>
                            ) : (
                                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                                    {body}
                                </Typography.Paragraph>
                            )}
                        </Flex>
                    ) : (
                        <Typography.Text type="secondary">
                            No Brand Voice set. AI-generated content will use default behavior.
                        </Typography.Text>
                    ))}
            </Skeleton>

            <BrandVoiceEditor
                key={drawerKey}
                domainId={props.domainId}
                directive={directive}
                visible={showDrawer}
                onClose={() => setShowDrawer(false)}
                onSaved={(updated) => {
                    setDirective(updated)
                    setShowDrawer(false)
                }}
            />
        </Well>
    )
}
